import React from 'react';
import { keepPreviousData } from '@tanstack/react-query';
import { useLocation } from '@/contexts/LocationContext';
import { useOrganization } from '@/contexts/OrganizationContext';
import { useLogger } from '@/lib/logger';
import { trpc, type RouterOutputs } from '@/lib/trpc';
import type { BaseGarmentAudience } from '@threadheads/shared';

type InventoryItem = RouterOutputs['inventory']['getProducts'][number];

/**
 * Hook for inventory read operations (queries)
 * Handles fetching products with caching and logging
 *
 * `productionType` (Inventory page type filter, ace/trims-ux): optional
 * server-side narrowing to POD / PAP / Trims. Threaded straight into the
 * query input so it's part of the tRPC/react-query cache key — a filter
 * change behaves exactly like a location switch (the `placeholderData:
 * keepPreviousData` + `isPlaceholderData` gating below already covers it).
 *
 * BLU-1189 `options.enabled`: the Inventory page keeps several modals MOUNTED
 * (they own exit animations and internal step state), and each of them needs a
 * catalogue of its own. With the page's type filter set, a modal's unfiltered
 * key differs from the page's, so an ungated subscription is a second full
 * catalogue fetch on page mount — the thing this ticket removed. Pass the
 * modal's open flag so the fetch happens when the operator actually opens it.
 * A gated caller whose key matches the page's still reads straight from cache.
 *
 * `options.who` (BLU-1394 F25): "Who can use it" for blanks, a query input
 * like the type filter (the server filters the whole set; the table pages it).
 */
export interface InventoryWhoInput {
  audience?: BaseGarmentAudience;
  clientOrganizationId?: string;
}

export function useInventoryQueries(
  productionType?: 'PAP' | 'POD' | 'TRIM',
  options?: { enabled?: boolean; who?: InventoryWhoInput },
) {
  const { selectedLocation, isLoading: locationsLoading } = useLocation();
  const { organization } = useOrganization();
  const logger = useLogger();

  // Use tRPC query to fetch products
  // When selectedLocation is null, omit locationId to fetch all locations (aggregated)
  const audience = options?.who?.audience;
  const clientOrganizationId = options?.who?.clientOrganizationId;
  const queryInput = React.useMemo(
    () => ({
      ...(selectedLocation ? { locationId: selectedLocation.id } : {}),
      ...(productionType ? { productionType } : {}),
      ...(audience ? { audience } : {}),
      ...(audience === 'client' && clientOrganizationId ? { clientOrganizationId } : {}),
    }),
    [selectedLocation, productionType, audience, clientOrganizationId]
  );

  const {
    data: rawProducts,
    isLoading: loading,
    isFetching,
    isPlaceholderData,
    error,
    refetch
  } = trpc.inventory.getProducts.useQuery(
    queryInput,
    {
      // BLU-3xx Phase 47.01.2-B-fix3.6 (#5): gate on `!locationsLoading` so
      // the initial fetch waits for LocationContext to settle. Pre-fix3.6,
      // org loaded fast → query fired with `selectedLocation === null` →
      // "All Locations" mode → wider/wrong dataset rendered for ~3s before
      // LocationContext hydrated the default and the query refetched.
      // After init, `selectedLocation === null` is honoured as a deliberate
      // "All Locations" choice (no behaviour change).
      enabled: (options?.enabled ?? true) && !!organization && !locationsLoading,
      staleTime: 30000, // 30 seconds
      retry: 2,
      // Avoid a "hard" reload on location switch; keep prior data while fetching the new location.
      placeholderData: keepPreviousData,
    }
  );

  // BLU-1189: the location-warming prefetch loop that used to live here is
  // gone. It ran after the first successful fetch and awaited one FULL
  // catalogue fetch per accessible location, sequentially — at Threadheads
  // (8 locations, ~2,400 rows each) that queued up to 8 extra multi-megabyte
  // requests per mount, and every consumer of this hook ran its own copy, so
  // the single backend instance spent minutes serving a page the operator was
  // already looking at. `staleTime` + `placeholderData: keepPreviousData`
  // already make a revisit to an already-fetched location cheap.

  // Log fetch success/errors (React Query v5 removed onSuccess/onError)
  React.useEffect(() => {
    if (loading) return;

    if (rawProducts && !error) {
      const lowStockItems = rawProducts.filter(p =>
        typeof p.notify === 'number' && p.stockLevel <= p.notify
      ).length;

      logger.info('Inventory data fetched successfully', {
        locationId: selectedLocation?.id ?? 'all',
        locationName: selectedLocation?.name ?? 'All Locations',
        itemCount: rawProducts.length,
        lowStockItems,
        operation: 'fetch_inventory'
      });

      if (lowStockItems > 0) {
        logger.info('Low stock items detected', {
          locationId: selectedLocation?.id ?? 'all',
          locationName: selectedLocation?.name ?? 'All Locations',
          lowStockCount: lowStockItems,
          businessImpact: 'inventory_management_required'
        });
      }
    }

    if (error) {
      logger.error('Failed to fetch inventory data', {
        locationId: selectedLocation?.id ?? 'all',
        locationName: selectedLocation?.name ?? 'All Locations',
        error: error instanceof Error ? error.message : 'Unknown error',
        operation: 'fetch_inventory'
      });
    }
  }, [rawProducts, error, loading, selectedLocation, logger]);

  // Transform tRPC response to Product[] format
  // Backend already returns category as nested object, just pass it through
  const products = React.useMemo(() =>
    rawProducts?.map((item: InventoryItem) => ({
      id: item.id,
      sku: item.sku,
      title: item.title ?? undefined,
      category: item.category || { type: undefined, size: undefined, color: undefined },
      stockLevel: item.stockLevel,
      onHand: item.onHand,
      available: item.available,
      committed: item.committed,
      unavailable: item.unavailable,
      incoming: item.incoming,
      image_url: item.imageUrl ?? undefined,
      notify: item.notify ?? undefined,
      updated_at: item.updatedAt,
      skuCostOverride: item.skuCostOverride ?? null,
      effectiveCost: item.effectiveCost ?? undefined,
      weightedAverageCost: item.weightedAverageCost ?? undefined,
      isLocked: item.isLocked ?? undefined,
      productionType: item.productionType ?? 'POD',
      isKeyItem: item.isKeyItem ?? false,
      isPreprinted: item.isPreprinted ?? false,
      isTrim: item.isTrim ?? false,
      // BLU-3xx Phase 47.01.2-B-03: pass through the row owner org so the
      // inventory table can render owner identity at shared 3PL locations.
      ownerOrganizationId: item.ownerOrganizationId ?? undefined,
      // BLU-711 Part B: shared-SKU enrichment. Without this passthrough the
      // backend-computed "+N via {Owner}" hint and the count-sheet collapse
      // key were silently stripped before InventoryTableList ever saw them.
      fallbackAvailable: item.fallbackAvailable ?? null,
      fallbackOwnerName: item.fallbackOwnerName ?? null,
      sharedPairId: item.sharedPairId ?? null,
      sharedPairRole: item.sharedPairRole ?? null,
      // Phase 92 (D-07, D-13): supply alias marker and the client's pool
      // figure (before hold-back). Same passthrough rule as BLU-711 above:
      // a field left out here never reaches the table or the count sheet.
      supplyAlias: item.supplyAlias ?? null,
      supplyPoolAvailable: item.supplyPoolAvailable ?? null,
      // BLU-1394 F25: the client a blank is kept for (own rows only; null otherwise).
      reservedForOrganizationId: item.reservedForOrganizationId ?? null,
      // BLU-1268: Supply. Same passthrough rule: without these a just-in-time
      // pair would show the store constant as a count.
      stockMode: item.stockMode ?? null,
      fallbackJustInTime: item.fallbackJustInTime === true,
      // BLU-1527 S3: an own substitute's figure names the substitute, not a 3PL.
      fallbackKind: item.fallbackKind ?? null,
      fallbackSubstituteSku: item.fallbackSubstituteSku ?? null,
      supplyPoolJustInTime: item.supplyPoolJustInTime === true,
    })) || [],
  [rawProducts]);

  return {
    products,
    // Consider loading until org is available (query is disabled until then).
    //
    // BLU-333: `placeholderData: keepPreviousData` above deliberately serves
    // the LAST successful fetch's data while a location switch's fetch is in
    // flight, to skip a full reload — but `isLoading` is false for that
    // entire window (react-query reports it as `status: 'success'`), so
    // without the `isPlaceholderData` check the table silently rendered the
    // PREVIOUS location's (or "All Locations"'s) numbers as if they belonged
    // to the newly-selected one. Folding `isPlaceholderData` in here is what
    // actually gates the display — the table shows its own loading skeleton
    // for that window instead of a trust-in-numbers bug, while a same-key
    // background refetch (staleTime expiry, window refocus) never sets
    // `isPlaceholderData` and stays flicker-free as before.
    loading: (loading && !rawProducts) || !organization || isPlaceholderData,
    isFetching,
    error,
    refreshInventory: refetch,
    selectedLocation
  };
}
